import {
  type KeyboardEventHandler,
  type MouseEventHandler,
  useCallback,
  useMemo,
} from 'react';
import { useCreateCollectionAndRedirect } from '@frameio/collections/operations/contexts';
import { useProjectPermissions } from '@frameio/permissions';
import { useActiveProjectId } from '@frameio/shared/contexts';
import { getLink } from '@frameio/shared/routing';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import { consumeEvent } from '@frameio/utils';
import { IconButton2, Text, theme, Tooltip } from '@frameio/vapor';
import { IconCollections, IconPlus } from '@frameio/vapor-icons';
import { useIsActiveItem, useOnItemSelect, useToggleItem } from '../contexts';
import type { AllCollectionsTreeItemProps, ProjectTreeItem } from '../types';
import TEXT from './generated/TEXT/AllCollections';
import {
  BaseContainer,
  HiddenLink,
  ICON_WIDTH,
  ItemContainer,
} from './TreeItem';

export const AllCollections = ({
  id,
  isOpen,
  transition,
  type,
}: AllCollectionsTreeItemProps) => {
  const item = useMemo<ProjectTreeItem>(() => ({ id, type }), [id, type]);

  const projectId = useActiveProjectId();
  const isActive = useIsActiveItem(item);
  const onItemSelect = useOnItemSelect();
  const toggleItem = useToggleItem();
  const track = useTrackUnsafe();

  const href = useMemo(
    () =>
      getLink({
        page: 'allCollections',
        projectId,
      }),
    [projectId]
  );

  const isDisabled = transition === 'close';

  const handleToggle = () => {
    toggleItem?.(item, !isOpen);
  };

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    let eventHandled = false;

    if (event.key === 'Enter') {
      onItemSelect(item, href);
      eventHandled = true;
    }

    if (event.key === 'ArrowRight') {
      toggleItem?.(item, true);
      eventHandled = true;
    }

    if (event.key === 'ArrowLeft') {
      toggleItem?.(item, false);
      eventHandled = true;
    }

    if (eventHandled) {
      consumeEvent(event);
    }
  };

  const handleClick: MouseEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      if (event.button === 2 || event.metaKey) return;

      track('collections-list-button-clicked');
      onItemSelect(item, href);
    },
    [href, item, onItemSelect, track]
  );

  const { isLoading: isLoadingMutation, createCollectionAndRedirect } =
    useCreateCollectionAndRedirect();

  const canCreateTeamCollection = useProjectPermissions({
    check: 'canCreateCollection',
    projectId,
  });

  const handleCreateCollectionPress = useCallback(
    () =>
      createCollectionAndRedirect({
        projectId,
        private: !canCreateTeamCollection,
      }),
    [projectId, createCollectionAndRedirect, canCreateTeamCollection]
  );

  return (
    <BaseContainer
      key={id}
      $isActive={isActive}
      onClick={handleClick}
      onContextMenu={consumeEvent}
      onKeyDown={handleKeyDown}
    >
      <ItemContainer
        key={id}
        adornmentAfter={
          <Tooltip placement="right" title={TEXT.createCollection}>
            <IconButton2
              data-testid="new-collection-button"
              icon={<IconPlus />}
              isDisabled={isLoadingMutation}
              label={TEXT.createCollection}
              onPress={handleCreateCollectionPress}
              size="small"
              variant="tertiary"
            />
          </Tooltip>
        }
        expandable={true}
        id={id}
        isActive={isActive}
        isDisabled={isDisabled}
        isLink={true}
        isOpen={isOpen}
        name={TEXT.collections}
        onChevronClick={handleToggle}
        type={type}
      >
        <IconCollections
          color={theme.colors.icon.primary}
          size={ICON_WIDTH}
          style={{ flexShrink: 0 }}
        />
        <Text
          color={isActive ? 'primary' : undefined}
          truncate={true}
          variant="body"
        >
          {TEXT.collections}
        </Text>

        {href && (
          <HiddenLink aria-label={TEXT.collections} href={href} tabIndex={-1} />
        )}
      </ItemContainer>
    </BaseContainer>
  );
};

TEXT.DEFINE({
  /** Label for the Collections link in the ProjectTree */
  collections: 'Collections',
  /** Tooltip and aria-label for creating a collection */
  createCollection: 'New Collection',
});
